import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ChoiceChips, ErrorState, Field, Hint, Icon, Input, Label, Select, Sheet, SheetContent, Skeleton, Textarea, toast } from "@sagot/ui";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "../../../lib/zod-resolver";
import { useErrorMessage } from "../../../lib/api-errors";
import { useAgentList, useAudienceList } from "../shared/use-engage-data";
import { useAnswerSchema, type AnswerValues } from "./answer-schema";
import { ClusterQuestions } from "./cluster-questions";
import { EXPIRY_CHOICES, expiryDate, type GapView } from "./gap-model";
import { useGapDetail, useResolveGap, useSuggestAnswer } from "./use-gaps";

interface WriteAnswerSheetProps {
  gap: GapView | null;
  onOpenChange: (open: boolean) => void;
  uploadHref: string | null;
}

/** "Write answer" drawer: answer → Q&A source that resolves the cluster. */
export function WriteAnswerSheet({ gap, onOpenChange, uploadHref }: WriteAnswerSheetProps) {
  const { t } = useTranslation();
  return (
    <Sheet open={gap !== null} onOpenChange={onOpenChange}>
      {gap ? (
        <SheetContent className="max-w-xl" title={gap.label} description={t("engage.gaps.sheet.eyebrow")} closeLabel={t("common.close")}>
          <AnswerForm
            key={gap.id}
            gap={gap}
            uploadHref={uploadHref}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

const FORM_ID = "gap-answer-form";

function AnswerForm({ gap, uploadHref, onDone }: { gap: GapView; uploadHref: string | null; onDone: () => void }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const detail = useGapDetail(gap.id);
  const agents = useAgentList();
  const audiences = useAudienceList();
  const suggest = useSuggestAnswer();
  const resolve = useResolveGap();
  const defaultAudience = audiences.data?.find((a) => a.is_default) ?? audiences.data?.[0];
  const { register, handleSubmit, control, setValue, formState } = useForm<AnswerValues>({
    resolver: zodResolver(useAnswerSchema()),
    defaultValues: { answer: "", audience: "", expiry: "never", customDate: "", agentIds: gap.agent_id ? [gap.agent_id] : [] },
  });
  const expiry = useWatch({ control, name: "expiry" });

  const onSuggest = () => {
    suggest.mutate(gap.id, {
      onSuccess: (draft) => {
        if (draft.no_source || !draft.draft) {
          toast.info(t("engage.gaps.sheet.noSuggestion"));
          return;
        }
        setValue("answer", draft.draft, { shouldValidate: true, shouldDirty: true });
        toast.info(t("engage.gaps.sheet.suggested", { count: draft.sources.length }));
      },
      onError: (error) => toast.error(errorMessage(error)),
    });
  };

  const onSubmit = handleSubmit((values) => {
    const audienceCode = Number(values.audience || defaultAudience?.code);
    const audienceLabel = audiences.data?.find((a) => a.code === audienceCode)?.label ?? "";
    const body = {
      answer: values.answer.trim(),
      agent_ids: values.agentIds,
      audiences: Number.isFinite(audienceCode) ? [audienceCode] : [],
      valid_until: expiryDate(values.expiry, values.customDate),
    };
    resolve.mutate(
      { gapId: gap.id, body },
      {
        onSuccess: () => {
          toast.success(t("engage.gaps.sheet.saved", { audience: audienceLabel }));
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  });

  return (
    <div className="flex h-full flex-col">
      <form id={FORM_ID} className="flex-1 space-y-5" onSubmit={(event) => void onSubmit(event)} noValidate>
        {detail.isPending ? (
          <Skeleton className="h-28" />
        ) : detail.isError ? (
          <ErrorState headingLevel={3} title={t("engage.gaps.sheet.loadError")} onRetry={() => void detail.refetch()} retryLabel={t("common.retry")} />
        ) : (
          <ClusterQuestions questions={detail.data.questions} fallback={gap.example_questions} />
        )}
        <div>
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="gap-answer" className="mb-0">
              {t("engage.gaps.sheet.answer")}
            </Label>
            <Button type="button" variant="ghost" size="sm" loading={suggest.isPending} onClick={onSuggest}>
              <Icon name="sparkles" />
              {t("engage.gaps.sheet.suggest")}
            </Button>
          </div>
          <Textarea
            id="gap-answer"
            rows={8}
            className="mt-1.5"
            placeholder={t("engage.gaps.sheet.answerPlaceholder")}
            aria-describedby="gap-answer-hint"
            invalid={Boolean(formState.errors.answer)}
            {...register("answer")}
          />
          {formState.errors.answer ? (
            <p className="mt-1.5 text-xs font-semibold text-danger" role="alert">
              {formState.errors.answer.message}
            </p>
          ) : null}
          <Hint id="gap-answer-hint">{t("engage.gaps.sheet.answerTip")}</Hint>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("engage.gaps.sheet.access")}>
            <Select {...register("audience")}>
              {(audiences.data ?? []).map((a) => (
                <option key={a.code} value={a.is_default ? "" : String(a.code)}>
                  {a.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("engage.gaps.sheet.expires")}>
            <Select {...register("expiry")}>
              {EXPIRY_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {t(`engage.gaps.sheet.expiry.${choice}`)}
                </option>
              ))}
            </Select>
          </Field>
          {expiry === "custom" ? (
            <Field label={t("engage.gaps.sheet.customDate")} error={formState.errors.customDate?.message}>
              <Input type="date" {...register("customDate")} />
            </Field>
          ) : null}
        </div>
        <div>
          <div className="label" id="gap-agents-label">
            {t("engage.gaps.sheet.agents")}
          </div>
          <Controller
            control={control}
            name="agentIds"
            render={({ field }) => (
              <ChoiceChips
                multiple
                aria-label={t("engage.gaps.sheet.agents")}
                options={(agents.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
                value={field.value}
                onValueChange={field.onChange}
              />
            )}
          />
          {formState.errors.agentIds ? (
            <p className="mt-1.5 text-xs font-semibold text-danger" role="alert">
              {formState.errors.agentIds.message}
            </p>
          ) : null}
        </div>
      </form>
      <div className="sticky -bottom-4 -mx-5 -mb-4 mt-5 flex bg-surface flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-4">
        {uploadHref ? (
          <Button asChild variant="ghost">
            <AppLink href={uploadHref}>
              <Icon name="upload" />
              {t("engage.gaps.sheet.uploadInstead")}
            </AppLink>
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onDone}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} loading={resolve.isPending}>
            <Icon name="check" />
            {t("engage.gaps.sheet.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
